import { arrayOf, bool, number, shape, string } from "prop-types";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { ErrorComponentWithContainer } from "web/Layout/Common/ErrorComponent";
import Loading from "web/Layout/Common/Loading/loading";
import ErrorBoundary from "web/Layout/ErrorBoundary";

import {
  useDataCachedProductsDomesticTourism,
  useDataCachedProductsInList,
  useDataCachedProductsTypes,
} from "web/hooks/useDataCached";

import jsonParse from "web/utils/data/parser/string/jsonParse";
import isArrayHasItems from "web/utils/data/validator/array/isArrayHasItems";

import TwoProducts from "./twoProducts";

const PageGeneratorTwoProductsContainerWithProducts = ({
  params,
  ids,
  isAnixeTourism,
  isMobile,
  storeConfig,
  listingPosition,
  listingAmount,
}) => {
  const { token, id: storeId } = storeConfig;
  const getProductsCache = isAnixeTourism
    ? useDataCachedProductsDomesticTourism
    : useDataCachedProductsInList;

  const { loading, error, data } = getProductsCache({
    ids,
    token,
    storeId,
  });

  const dataTruncatedToTwoProducts = data.slice(0, 2);

  const {
    button,
    color_background: backgroundColor,
    color_font_header: headerFontColor,
    color_font_description: descriptionFontColor,
    color_font_button: buttonFontColor,
    color_background_button: buttonBackgroundColor,
    graphic_file: imageSrc,
    description,
    header,
    height,
    layout,
    url,
  } = useMemo(() => jsonParse(params), [params]);

  if (loading) {
    return (
      <div style={{ height: 830 }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    console.error(error);
    return <ErrorComponentWithContainer />;
  }

  return (
    <TwoProducts
      isMobile={isMobile}
      backgroundColor={backgroundColor}
      descriptionFontColor={descriptionFontColor}
      imageSrc={imageSrc}
      height={height}
      header={header}
      description={description}
      button={button}
      url={url}
      layout={layout}
      headerFontColor={headerFontColor}
      buttonFontColor={buttonFontColor}
      buttonBackgroundColor={buttonBackgroundColor}
      data={dataTruncatedToTwoProducts}
      listingPosition={listingPosition}
      listingAmount={listingAmount}
    />
  );
};

PageGeneratorTwoProductsContainerWithProducts.propTypes = {
  params: string.isRequired,
  isMobile: bool,
  ids: arrayOf(number).isRequired,
  isAnixeTourism: bool,
  storeConfig: shape({}),
  listingPosition: number,
  listingAmount: number,
};

PageGeneratorTwoProductsContainerWithProducts.defaultProps = {
  isMobile: false,
  isAnixeTourism: false,
  storeConfig: {},
  listingPosition: null,
  listingAmount: null,
};

const PageGeneratorTwoProductsContainer = ({
  params,
  isMobile,
  storeConfig,
  listingPosition,
  listingAmount,
}) => {
  const { product_id: productId } = useMemo(() => jsonParse(params), [params]);

  const ids = useMemo(() => {
    return productId.split(",").map((id) => +id);
  }, [productId]);

  const { token, id: storeId } = storeConfig;
  const { loading, error, data } = useDataCachedProductsTypes({
    ids,
    token,
    storeId,
  });

  if (loading) {
    return (
      <div style={{ height: 830 }}>
        <Loading />
      </div>
    );
  }

  if (error) {
    console.error(error);
    return <ErrorComponentWithContainer />;
  }

  const isAnixeTourism =
    data &&
    isArrayHasItems(data) &&
    data.some((item) => item && item.mb_product_type === "anixe_tourism");

  return (
    <PageGeneratorTwoProductsContainerWithProducts
      params={params}
      isMobile={isMobile}
      ids={ids}
      isAnixeTourism={isAnixeTourism}
      storeConfig={storeConfig}
      listingPosition={listingPosition}
      listingAmount={listingAmount}
    />
  );
};

PageGeneratorTwoProductsContainer.propTypes = {
  params: string.isRequired,
  isMobile: bool,
  storeConfig: shape({}),
  listingPosition: number,
  listingAmount: number,
};

PageGeneratorTwoProductsContainer.defaultProps = {
  isMobile: false,
  storeConfig: {},
  listingPosition: null,
  listingAmount: null,
};

const PageGeneratorTwoProductsContainerError = ({
  params,
  isMobile,
  listingPosition,
  listingAmount,
}) => {
  const storeConfig = useSelector((state) => state.app.storeConfig);
  return (
    <ErrorBoundary>
      <PageGeneratorTwoProductsContainer
        params={params}
        isMobile={isMobile}
        storeConfig={storeConfig}
        listingPosition={listingPosition}
        listingAmount={listingAmount}
      />
    </ErrorBoundary>
  );
};

PageGeneratorTwoProductsContainerError.propTypes = {
  params: string.isRequired,
  isMobile: bool,
  listingPosition: number,
  listingAmount: number,
};

PageGeneratorTwoProductsContainerError.defaultProps = {
  isMobile: false,
  listingPosition: null,
  listingAmount: null,
};

export default PageGeneratorTwoProductsContainerError;
